/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { convertToRem } from '../../utils/pixel-to-rem-convertor.js';

/**
 * @prop {Boolean} condensed - If set to `true` , the condensed table style is applied
 * @prop {Boolean} freeze - If set to `true` , the frozen table style is applied
 * @prop {String} lwidth - Default 33.3%. Sets the max-width, width, min-width of the frozen-left block in a frozen table
 * @prop {String} mwidth - Default 33.3%. Sets the max-width, width, min-width of the scrollable block in a frozen table
 * @prop {String} rwidth - Default 33.3%. Sets the max-width, width, min-width of the frozen-right block in a frozen table
 * @prop {Boolean} readonly - If set to `true`, adds readonly styles to its children.
 * @prop {Boolean} multiselect - Allows multi-selection of rows if set to `true`.
 * @prop {Array} selectedRows - Currently selected rows.
 * @prop {String} selectionMode - 	Allows multi-selection through different modes. Possible value is metakey, which allows multi-selection using keyboard (Ctrl/Shift for Windows, Cmd/Shift for MacOs). This has to be used along with multiselect property.
 * @attr {String} selection-mode - 	Allows multi-selection through different modes. Possible value is metakey, which allows multi-selection using keyboard (Ctrl/Shift for Windows, Cmd/Shift for MacOs). This has to be used along with multiselect property.
 * @slot Takes sh-table-row as children.
 * @slot header - Takes sh-table-row as children. Will be rendered in a fixed position at the top of the table (won't scroll with the content). Meant to be the table's header.
 * @fires scroll-end - Dispatched when the scroll bar hits the bottom of the table.
 * @fires middle-scrolled - Dispatched when either the horizontal scrollbar or any row in the scrollable block is scrolled, in a frozen table.
 * @fires slots-modified - Dispatched when any of the frozen table slots content is modified / added.
 */
export class SHTable extends LitElement {
  static get properties() {
    return {
      lwidth: { type: String, reflect: true },
      rwidth: { type: String, reflect: true },
      mwidth: { type: String, reflect: true },
      freeze: { type: Boolean, reflect: true },
      /** @ignore */
      scrolledLeft: { type: Number },
      condensed: { type: Boolean, reflect: true },
      readonly: { type: Boolean, reflect: true },
      multiselect: { type: Boolean, reflect: true },
      selectionMode: {
        type: String,
        reflect: true,
        attribute: 'selection-mode',
      },
      selectedRows: { type: Array, reflect: false },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: 100%;
          height: 100%;
          overflow-y: hidden;
          display: -webkit-box;
          display: -ms-flexbox;
          display: flex;
          -webkit-box-orient: vertical;
          -webkit-box-direction: normal;
          -ms-flex-direction: column;
          flex-direction: column;
          --scrollbar-width: 0rem;
        }
        :host([multiselect][selection-mode='metakey']) {
          user-select: none;
        }
        :host([fit-content]) {
          min-height: fit-content;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
          max-height: fit-content;
          max-height: -ms-fit-content;
          max-height: -moz-fit-content;
        }

        .table-wrapper {
          -webkit-box-flex: 1;
          -ms-flex: 1;
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          width: 100%;
          -webkit-box-sizing: border-box;
          box-sizing: border-box;
        }
        .table-wrapper {
          max-height: -webkit-fit-content;
          max-height: -moz-fit-content;
          max-height: fit-content;
        }
        :host([freeze]) .floating-scrollbar {
          position: relative;
          margin-top: 0.5rem;
          overflow-x: auto;
        }
        :host([freeze]) .scrollThumb {
          height: 0.5rem;
        }
        .header-wrapper {
          overflow-x: hidden;
          width: 100%;
          max-height: -webkit-fit-content;
          max-height: -moz-fit-content;
          max-height: fit-content;
          overflow-y: hidden;
          padding-right: var(--scrollbar-width);
          -webkit-box-sizing: border-box;
          box-sizing: border-box;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.lwidth = '33.3%';
    this.rwidth = '33.3%';
    this.mwidth = '33.3%';
    this.selectedRows = [];
  }

  render() {
    return html`
      <div class="header-wrapper">
        <slot name="header" id="header-slot"></slot>
      </div>
      <div class="table-wrapper" @scroll="${() => this.contentScrolled()}" id="tableContent">
        <div class="content">
          <slot id="tableBodySlot"></slot>
        </div>
      </div>
      ${this.freeze
        ? html`
            <div class="floating-scrollbar" @scroll="${(e) => this.adjustScrollLeft(e)}">
              <div class="scrollThumb"></div>
            </div>
          `
        : ''}
    `;
  }

  attributeChangedCallback(name, oldval, newval) {
    super.attributeChangedCallback(name, oldval, newval);
    if (name === 'condensed') {
      this.condensedObserver();
    } else if (name === 'lwidth' || name === 'rwidth' || name === 'mwidth') {
      this.widthObserver();
    } else if (name === 'readonly') {
      this.slotObserver();
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('scrolledLeft')) {
      this.scrolledLeftChanged();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.resizeListener = this.windowResizeListener.bind(this);
    this.addEventListener('clicked', function () {
      if (!this.multiselect) {
        const childElement = this.querySelectorAll('sh-table-row[active]');
        for (let i = 0; i < childElement.length; i++) {
          childElement[i].removeAttribute('active');
        }
      }
    });
    if (this.freeze) {
      window.addEventListener('resize', this.resizeListener);
    }
    this._resizeObserver = new ResizeObserver(() => {
      this.windowResizeListener();
    });
  }

  firstUpdated() {
    const bodySlot = this.shadowRoot.querySelector('#tableBodySlot');
    bodySlot.addEventListener('slotchange', () => this.slotObserver());
    const baseFontSize = parseFloat(
      window.getComputedStyle(document.querySelector('html'), null).getPropertyValue('font-size')
    );
    if (this.freeze) {
      const floatingScrollBar = this.shadowRoot.querySelector('.floating-scrollbar');
      const floatThumb = this.shadowRoot.querySelector('.scrollThumb');
      this.addEventListener('slots-modified', function (e) {
        floatThumb.style.width = `${e.detail.innerWidth / baseFontSize}rem`;
        floatingScrollBar.style.width = `${e.detail.width / baseFontSize}rem`;
        floatingScrollBar.style.left = `${e.detail.leftPosition / baseFontSize}rem`;
        if (this.scrolledLeft !== undefined) {
          floatingScrollBar.scrollLeft = this.scrolledLeft;
          let allChildTableRows = this.querySelectorAll('sh-table-row');
          for (let i = 0; i < allChildTableRows.length; i++) {
            if (allChildTableRows[i].freeze) {
              allChildTableRows[i].shadowRoot.querySelector('.scrollable').scrollLeft =
                this.scrolledLeft;
            }
          }
        }
        this.widthObserver();
      });
      this.addEventListener('touchstart', (e) => {
        if (!this._hasAllFrozenRows()) {
          return;
        }
        let touchMovePathElements = e.composedPath();
        // a flag to identify if touch actually swiped on the scrollable part
        let swipedOnScrollable = false;
        for (let i = 0; i < touchMovePathElements.length; i++) {
          // adding check for touchMovePathElements[i].tagName since sometimes it will throw error for document nodes that don't have tags
          if (
            touchMovePathElements[i].tagName &&
            touchMovePathElements[i].classList.contains('scrollable') &&
            touchMovePathElements[i].tagName === 'DIV'
          ) {
            swipedOnScrollable = true;
            break;
          }
        }
        if (swipedOnScrollable) {
          // updating touchStartPos at the beginning of every swipe event ...
          this.touchStartPos = e.changedTouches[0].pageX;
          this.touchstartedOnScrollable = true;
        } else {
          this.touchstartedOnScrollable = false;
        }
      });

      this.addEventListener('touchmove', (e) => {
        if (!this._hasAllFrozenRows()) {
          if (!this._issuedFrozenTableWarning) {
            console.warn(`All table rows in a frozen table must have freeze property.`);
            this._issuedFrozenTableWarning = true;
          }
          return;
        }
        let touchMovePathElements = e.composedPath();
        if (this.touchstartedOnScrollable) {
          // a flag to identify if touch actually swiped on the scrollable part
          let swipedOnScrollable = false;
          for (let i = 0; i < touchMovePathElements.length; i++) {
            // adding check for touchMovePathElements[i].tagName since sometimes it will throw error for document nodes that don't have tags
            if (
              touchMovePathElements[i].tagName &&
              touchMovePathElements[i].classList.contains('scrollable') &&
              touchMovePathElements[i].tagName === 'DIV'
            ) {
              swipedOnScrollable = true;
              break;
            }
          }
          // e.changedTouches[0].pageX - touchStartPos is meant to be the delta needed to either add or subtract from the scrollLeft.
          // simply doing += to the scrollLeft didn't work...
          // that's why using different checks which direction the pointer is moving...
          // and then either subtracting or adding the absolute value of the delta...

          // maybe additional improvements or altogether better idea could also be deviced in the future...
          if (swipedOnScrollable) {
            if (e.changedTouches[0].pageX - this.touchStartPos < 0) {
              floatingScrollBar.scrollLeft += Math.abs(
                e.changedTouches[0].pageX - this.touchStartPos
              );
            } else {
              floatingScrollBar.scrollLeft -= Math.abs(
                e.changedTouches[0].pageX - this.touchStartPos
              );
            }
            // updating touchStartPos at the end of every touchmove ...
            this.touchStartPos = e.changedTouches[0].pageX;
          }
        }
      });
    }
    /**@ignore */
    this._issuedFrozenTableWarning = false;
    this._resizeObserver.observe(this);
  }

  setHeaderPadding() {
    const scrollBarWidthInPx = this.getScrollBarWidth();
    const scrollBarWidthInRem =
      typeof scrollBarWidthInPx === 'number' ? convertToRem(scrollBarWidthInPx) : null;
    const headerWrapperIsPresent = Boolean(this.shadowRoot.querySelector('.header-wrapper'));
    if (headerWrapperIsPresent && typeof scrollBarWidthInRem === 'number') {
      this.shadowRoot
        .querySelector('.header-wrapper')
        .style.setProperty('--scrollbar-width', `${scrollBarWidthInRem}rem`);
    }
  }

  getScrollBarWidth() {
    const tableWrapperIsPresent = Boolean(this.shadowRoot.querySelector('.table-wrapper'));
    if (tableWrapperIsPresent) {
      const tableWrapperWidth = this.shadowRoot
        .querySelector('.table-wrapper')
        .getBoundingClientRect().width;
      const tableWrapperContentWidth = this.shadowRoot
        .querySelector('.table-wrapper .content')
        .getBoundingClientRect().width;
      return tableWrapperWidth - tableWrapperContentWidth;
    } else {
      return null;
    }
  }

  windowResizeListener() {
    this.setHeaderPadding();
    this.forceStyleUpdate();
    setTimeout(() => {
      this.setHeaderPadding();
      this.forceStyleUpdate();
      if (this.freeze) {
        const floatingScrollBar = this.shadowRoot.querySelector('.floating-scrollbar');
        const floatThumb = this.shadowRoot.querySelector('.scrollThumb');
        // Selecting first row with freeze property as the sample
        // row to apply the scrollbar's width and internal thumb width
        let firstRowChild = this.querySelector('sh-table-row[freeze]');
        const baseFontSize = parseFloat(
          window
            .getComputedStyle(document.querySelector('html'), null)
            .getPropertyValue('font-size')
        );
        if (!firstRowChild) {
          return;
        }
        const frozenLeft = firstRowChild.shadowRoot.querySelector('.frozen-left');
        const scrollable = firstRowChild.shadowRoot.querySelector('.scrollable');
        floatingScrollBar.style.left = `${
          frozenLeft.getBoundingClientRect().width / baseFontSize
        }rem`;
        floatingScrollBar.style.width = `${
          scrollable.getBoundingClientRect().width / baseFontSize
        }rem`;
        floatThumb.style.width = `${scrollable.scrollWidth / baseFontSize}rem`;
      }
    }, 500);
  }
  forceStyleUpdate() {
    getComputedStyle(document.documentElement);
  }
  widthObserver() {
    let allChildTableRows = this.querySelectorAll('sh-table-row');
    for (let i = 0; i < allChildTableRows.length; i++) {
      if (allChildTableRows[i].freeze) {
        allChildTableRows[i].lwidth = this.lwidth;
        allChildTableRows[i].mwidth = this.mwidth;
        allChildTableRows[i].rwidth = this.rwidth;
      }
    }
    this.windowResizeListener();
  }

  condensedObserver() {
    let allChildTableRows = this.querySelectorAll('sh-table-row');
    for (let i = 0; i < allChildTableRows.length; i++) {
      allChildTableRows[i].condensed = this.condensed;
    }
  }

  contentScrolled() {
    const content = this.shadowRoot.querySelector('#tableContent');
    const contentScroll = content.scrollTop + content.clientHeight;
    const contentMaxScroll = content.scrollHeight;
    if (Math.ceil(contentScroll) >= contentMaxScroll) {
      this.dispatchEvent(
        new CustomEvent('scroll-end', {
          detail: this,
          composed: true,
          bubbles: true,
        })
      );
    }
  }

  slotObserver() {
    let allChildTableRows = this.querySelectorAll('sh-table-row');
    for (let i = 0; i < allChildTableRows.length; i++) {
      allChildTableRows[i].readonly = this.readonly;
    }
    this.windowResizeListener();
  }

  adjustScrollLeft(e) {
    let allChildTableRows = this.querySelectorAll('sh-table-row');
    if (!this._hasAllFrozenRows()) {
      if (!this._issuedFrozenTableWarning) {
        console.warn(`All table rows in a frozen table must have freeze property.`);
        this._issuedFrozenTableWarning = true;
      }
      return;
    }
    for (let i = 0; i < allChildTableRows.length; i++) {
      if (allChildTableRows[i].freeze) {
        allChildTableRows[i].shadowRoot.querySelector('.scrollable').scrollLeft =
          e.target.scrollLeft;
      }
    }
    this.scrolledLeft = e.target.scrollLeft;
  }

  scrolledLeftChanged() {
    let allChildTableRows = this.querySelectorAll('sh-table-row');
    if (!this._hasAllFrozenRows()) {
      return;
    }
    for (let i = 0; i < allChildTableRows.length; i++) {
      if (allChildTableRows[i].freeze) {
        allChildTableRows[i].scrollLeft = this.scrolledLeft;
        allChildTableRows[i].shadowRoot.querySelector('.scrollable').scrollLeft = this.scrolledLeft;
      }
    }
  }
  /**@ignore */
  _hasAllFrozenRows() {
    /**
     * If any sh-table-row is present without freeze property, then all
     * rows are not frozen.
     * Else, all rows are frozen
     */
    const areAllRowsFrozen = this.querySelector('sh-table-row:not([freeze])') ? false : true;
    return areAllRowsFrozen;
  }

  disconnectedCallback() {
    if (this.freeze) {
      window.removeEventListener('resize', this.resizeListener);
    }
    this._resizeObserver.disconnect();
    super.disconnectedCallback();
  }
}

customElements.define('sh-table', SHTable);
